/**
 * This React provider checks that the user is logged in. It allows child
 * components to call useAdmin() and useTenant() to get information about the
 * logged-in admin and their tenant.
 */
import { useQuery } from "@apollo/client";
import React, { ReactElement, ReactNode, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  GetSubaccountAdminFieldsQuery,
  GetSubaccountAdminFieldsQueryVariables,
  GetSubaccountBaseFieldsQuery,
  GetSubaccountBaseFieldsQueryVariables,
  GetSubaccountSupervolunteerFieldsQuery,
  GetSubaccountSupervolunteerFieldsQueryVariables,
} from "~frontend/__generated__/graphql";
import FullPageCard from "~frontend/components/FullPageCard";
import Loading from "~frontend/components/Loading";
import {
  GET_SUBACCOUNT_ADMIN_FIELDS,
  GET_SUBACCOUNT_BASE_FIELDS,
  GET_SUBACCOUNT_SUPERVOLUNTEER_FIELDS,
} from "~frontend/graphql/subaccounts";
import { getApolloErrorMessage } from "~frontend/util/errors";
import {
  userIsSubaccountAdminOrHigher,
  userIsSubaccountSupervolunteerOrHigher,
} from "~frontend/util/roles";
import { setLastUsedTimeForSubaccount } from "~frontend/util/subaccountUsage";

import { useIntl } from "./IntlProvider";
import {
  SubaccountRole,
  UserSubaccountMembership,
  useTenant,
} from "./LoggedInUserProvider";
import {
  SubaccountAdminData,
  SubaccountBaseData,
  SubaccountSupervolunteerData,
  useCurrentUserSubaccountMemberships,
} from "./LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "./NonCacheClearingApolloClientProvider";

type SubaccountContextValue = {
  membership: UserSubaccountMembership;
  subaccount: SubaccountBaseData;
  role: SubaccountRole;
  subaccountLink: (relativePath: string) => string;
  landingPage: string;
  donationHistoryPageEnabled?: boolean;
} | null;

export const SubaccountContext =
  React.createContext<SubaccountContextValue>(null);

export function useSubaccount(): SubaccountContextValue | null {
  return useContext(SubaccountContext);
}

type SubaccountSupervolunteerConfigContextValue =
  SubaccountSupervolunteerData | null;

export const SubaccountSupervolunteerConfigContext =
  React.createContext<SubaccountSupervolunteerConfigContextValue>(null);

export function useSubaccountSupervolunteerConfig(): SubaccountSupervolunteerConfigContextValue {
  return useContext(SubaccountSupervolunteerConfigContext);
}

export type SubaccountAdminConfigContextValue = SubaccountAdminData | null;

export const SubaccountAdminConfigContext =
  React.createContext<SubaccountAdminConfigContextValue>(null);

export function useSubaccountAdminConfig(): SubaccountAdminConfigContextValue {
  return useContext(SubaccountAdminConfigContext);
}

function ErrorPage({ error }: { error: ReactNode }): ReactElement {
  return <FullPageCard title="Error">{error}</FullPageCard>;
}

export function subaccountLandingPage(
  membership: UserSubaccountMembership
): string {
  if (userIsSubaccountAdminOrHigher(membership)) {
    return "campaigns";
  } else if (userIsSubaccountSupervolunteerOrHigher(membership)) {
    return "threads";
  } else {
    return "inboxes";
  }
}

export default function SubaccountProvider({
  children,
  explicitSubaccountSlug,
}: {
  children: React.ReactNode;
  explicitSubaccountSlug: string | null;
}): ReactElement {
  const client = useNonCacheClearingApolloClient();

  const intl = useIntl();
  const navigate = useNavigate();
  const tenant = useTenant();
  const memberships = useCurrentUserSubaccountMemberships();
  if (!memberships || !tenant) {
    throw new Error(
      "Cannot render SubaccountProvider without tenant and memberships"
    );
  }

  let membership: UserSubaccountMembership | undefined;
  if (explicitSubaccountSlug === null) {
    // no explicit subaccount ID -- use the default subaccount
    membership = memberships.find(
      membership => membership.subaccount.id === tenant.defaultSubaccount?.id
    );
  } else {
    membership = memberships.find(
      membership => membership.subaccount.slug === explicitSubaccountSlug
    );
  }

  let redirectDestination: string | undefined;
  let routingError: string | ReactElement | undefined;
  if (!membership) {
    // we couldn't find a matching membership. Redirect the user somewhere else.
    if (tenant.defaultSubaccount) {
      // the tenant is in non-subaccount mode, so redirect to the non-subaccount
      // routes. But first check that they have a membership in that subaccount.
      const subaccountMembership = memberships.find(
        membership => membership.subaccount.id === tenant.defaultSubaccount?.id
      );
      if (subaccountMembership) {
        redirectDestination = `/admin/${subaccountLandingPage(subaccountMembership)}`;
      } else {
        routingError =
          "You do not have access to the default subaccount for this organization. Please contact Scale to Win support.";
      }
    } else {
      // The tenant is in subaccount mode. If the user has only one membership,
      // send them there. Otherwise, send them to the subaccount list.
      if (memberships.length === 0) {
        routingError =
          "You do not have access to any subaccounts for this organization. Please contact Scale to Win support.";
      } else if (memberships.length === 1) {
        redirectDestination = `/subaccounts/${memberships[0].subaccount.slug}/${subaccountLandingPage(
          memberships[0]
        )}`;
      } else {
        redirectDestination = `/user/subaccounts`;
      }
    }
  }

  useEffect(() => {
    if (redirectDestination) {
      navigate(redirectDestination)?.catch(console.error);
    }
  }, [redirectDestination, navigate]);

  useEffect(() => {
    if (membership?.subaccount.id) {
      setLastUsedTimeForSubaccount(membership.subaccount.id);
    }
  }, [membership?.subaccount.id]);

  const {
    data: baseData,
    loading: baseDataLoading,
    error: baseDataError,
  } = useQuery<
    GetSubaccountBaseFieldsQuery,
    GetSubaccountBaseFieldsQueryVariables
  >(GET_SUBACCOUNT_BASE_FIELDS, {
    variables: {
      input: {
        subaccountId: membership?.subaccount.id || "",
      },
    },
    skip: !membership,
    client,
  });

  const {
    data: supervolunteerData,
    loading: supervolunteerDataLoading,
    error: supervolunteerDataError,
  } = useQuery<
    GetSubaccountSupervolunteerFieldsQuery,
    GetSubaccountSupervolunteerFieldsQueryVariables
  >(GET_SUBACCOUNT_SUPERVOLUNTEER_FIELDS, {
    variables: {
      input: {
        subaccountId: membership?.subaccount.id || "",
      },
    },
    skip: !membership || !userIsSubaccountSupervolunteerOrHigher(membership),
    client,
  });

  const {
    data: adminData,
    loading: adminDataLoading,
    error: adminDataError,
  } = useQuery<
    GetSubaccountAdminFieldsQuery,
    GetSubaccountAdminFieldsQueryVariables
  >(GET_SUBACCOUNT_ADMIN_FIELDS, {
    variables: {
      input: {
        subaccountId: membership?.subaccount.id || "",
      },
    },
    skip: !membership || !userIsSubaccountAdminOrHigher(membership),
    client,
  });

  if (
    baseDataError ||
    supervolunteerDataError ||
    adminDataError ||
    routingError
  ) {
    return (
      <ErrorPage
        error={
          (baseDataError && getApolloErrorMessage(baseDataError, intl)) ||
          (supervolunteerDataError &&
            getApolloErrorMessage(supervolunteerDataError, intl)) ||
          (adminDataError && getApolloErrorMessage(adminDataError, intl)) ||
          routingError
        }
      />
    );
  }

  if (!membership) {
    // we will redirect above
    return <div />;
  }

  if (baseDataLoading || supervolunteerDataLoading || adminDataLoading) {
    return <Loading />;
  }

  function subaccountLink(relativePath: string): string {
    if (explicitSubaccountSlug) {
      return `/subaccounts/${explicitSubaccountSlug}${relativePath}`;
    } else {
      return `/admin${relativePath}`;
    }
  }

  return (
    <SubaccountContext.Provider
      value={{
        membership,
        subaccount: baseData!.getSubaccount,
        role: membership.role,
        subaccountLink,
        landingPage: subaccountLink(`/${subaccountLandingPage(membership)}`),
        donationHistoryPageEnabled:
          adminData?.getSubaccount.donationHistoryPageEnabled,
      }}
    >
      <SubaccountSupervolunteerConfigContext.Provider
        value={supervolunteerData?.getSubaccount ?? null}
      >
        <SubaccountAdminConfigContext.Provider
          value={adminData?.getSubaccount ?? null}
        >
          {children}
        </SubaccountAdminConfigContext.Provider>
      </SubaccountSupervolunteerConfigContext.Provider>
    </SubaccountContext.Provider>
  );
}
